import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import {
  EmptyIndicator,
  LoadingIndicator,
  TitleWithIcon
} from 'components/common';
import { BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import { MyTeamFollowBox, useMyTeamFollowContext } from '.';

import type {
  GetTeamPathsCategoriesRes,
  GetTeamPathsRes
} from 'shared-types/responses';

const MyTeamPaths = () => {
  const { messages, user } = useReduxState();

  const { queryString, queryParams } = useMyTeamFollowContext();

  const { data, loading } = useApiData<GetTeamPathsRes>({
    url:
      `/admin/supervisors/${user.id}/team-paths` +
      (queryString.length ? `?${queryString}` : ''),
    onlyIf: Boolean(user.id)
  });

  const { data: pathCategories } = useApiData<GetTeamPathsCategoriesRes>({
    url: `/admin/supervisors/${user.id}/team-paths-categories`,
    onlyIf: Boolean(user?.id)
  });

  return (
    <MyTeamFollowBox
      numberOfPages={data?.paths?.length ? data?.pages || 1 : 0}
      title={messages.commonPaths}
      {...(pathCategories?.categories?.length
        ? {
            customFilters: [
              {
                allowMultiple: true,
                field: 'categoryId',
                icon: 'tag' as const,
                items: pathCategories?.categories?.map((category) => ({
                  label: category.title,
                  value: `${category.id}`
                })),
                position: 0,
                title: messages.commonCategory
              }
            ]
          }
        : {})}
    >
      {loading ? (
        <LoadingIndicator />
      ) : data?.paths?.length ? (
        data.paths.map((path) => (
          <BoxRow key={path.id}>
            <div className="row-sm row-center">
              <div className="col">
                <TitleWithIcon
                  icon="tasks"
                  title={path.title}
                  titleLink={{
                    as: `/my-team/follow/paths/${path.id}`,
                    href: '/my-team/follow/paths/[pathId]'
                  }}
                  titleStyle={{ fontSize: '1.5rem' }}
                >
                  {getMessageWithPlaceholders({
                    message: messages.pathsNumberOfSteps,
                    placeholders: { numberOfSteps: path.steps?.length }
                  })}
                </TitleWithIcon>
              </div>
              <div className="col-min">
                <ParticipantsStats
                  completionEnabled={true}
                  progressData={path.progressData}
                  notStartedLabel={
                    queryParams['participantsOnly'] === 'true'
                      ? messages.tasksUsersNotStartedSingular
                      : messages.commonNotStartedOrNotAdded
                  }
                />
              </div>
            </div>
          </BoxRow>
        ))
      ) : (
        <EmptyIndicator icon="tasks" title={messages.pathsEmpty} />
      )}
    </MyTeamFollowBox>
  );
};

export default MyTeamPaths;
